import React, { useState, useEffect } from "react";
import { Box, Typography, Button, IconButton, TextField, Chip, Grid, MenuItem } from "@mui/material";
import { ImageOutlined, AccessibilityNew, Delete, Download } from "@mui/icons-material";
import { useSelector } from "react-redux";
import ReusableDialog from "../../../../common/dialog/dialog";
import MaleBodyMap from "./BodyMapCauseForConcern/male-body-map";
import FemaleBodyMap from "./BodyMapCauseForConcern/female-body-map";

const CauseForImage = ({ updateFormData, prevStep, handleSubmit, formData, files, images, person }) => {
  const user = useSelector((state) => state.auth.user); // User data from Redux store
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(person || "");
  const [bodyMapOpen, setBodyMapOpen] = useState(false);
  const [incidentRecords, setIncidentRecords] = useState([]); // State to store records
  const [uploadedImages, setUploadedImages] = useState([]); // State to store records

  useEffect(() => {
    if (files && files.length > 0) {
      setUploadedFiles(files);
    }
  }, [files]);

  useEffect(() => {
    if (images && images.length > 0) {
      setUploadedImages(images);
    }
  }, [images]);

  useEffect(() => {
    if (formData && formData.length > 0) {
      setIncidentRecords(formData);
    }
  }, [formData]);

  // Function to update incident records
  const handleIncidentSubmit = (newRecord) => {
    setIncidentRecords((prevRecords) => [...prevRecords, newRecord]);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFiles((prevFiles) => [...prevFiles, file]);
    }
  };

  const handleDeleteFile = (index) => {
    setUploadedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  // Add download handler
  const handleDownload = (file) => {
    try {
      // Create a URL for the file
      const fileUrl = URL.createObjectURL(file);

      // Create temporary link element
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = file.name; // Set downloaded file name

      // Append to document, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL
      URL.revokeObjectURL(fileUrl);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  // Add cleanup effect for any remaining object URLs
  useEffect(() => {
    return () => {
      uploadedFiles.forEach((file) => {
        if (file.url) URL.revokeObjectURL(file.url);
      });
    };
  }, [uploadedFiles]);

  const preparedFiles = uploadedFiles.map((file) => {
    let previewUrl = "";
    try {
      if (file instanceof File || file instanceof Blob) {
        previewUrl = URL.createObjectURL(file);
      }
    } catch (err) {
      console.error("Failed to create object URL", err);
    }

    return {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified,
      previewUrl,
      file,
    };
  });

  useEffect(() => {
    const urls = uploadedFiles
      .filter((file) => file instanceof File || file instanceof Blob)
      .map((file) => URL.createObjectURL(file));

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [uploadedFiles]);

  return (
    <Box sx={{ pl: 4, pr: 4, ml: 2 }}>
      <Chip
        variant='contained'
        sx={{
          fontSize: "14px",
          backgroundColor: "#F2F6FF",
          color: "blue",
        }}
        label='Daily Log'
        size='small'
      />
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold" }}>Cause For Concern</Typography>
      <Typography variant='subtitle2' sx={{ color: "#757575" }}>
        Date: {new Date().toLocaleDateString()} &nbsp; • &nbsp;{" "}
        <span style={{ color: "#1751D0" }}>{user.username}</span>
      </Typography>
      <Box sx={{ borderBottom: "1px solid #E0E0E0", mt: 2, mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item lg={8} md={8}>
          <Box sx={{ mb: 4 }}>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              If any Attachment:
            </Typography>
            <Box
              sx={{
                border: "2px dashed #E0E0E0",
                borderRadius: "8px",
                p: 4,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 6,
                minHeight: "160px",
                bgcolor: "#FFFFFF",
              }}
            >
              {/* Choose Picture Option */}
              <Box
                component='label'
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <input type='file' accept='image/*' hidden onChange={handleFileChange} id='file-input' />
                <IconButton
                  onClick={() => document.getElementById("file-input").click()}
                  sx={{
                    border: "1px solid #E0E0E0",
                    p: 2,
                    mb: 1,
                    "&:hover": { bgcolor: "#F5F5F5" },
                  }}
                >
                  <ImageOutlined sx={{ fontSize: 24, color: "#666" }} />
                </IconButton>
                <Typography variant='body2' color='text.secondary'>
                  Choose a picture
                </Typography>
              </Box>

              {/* Divider */}
              <Box sx={{ width: "1px", height: "60px", bgcolor: "#E0E0E0" }} />

              <Box
                component='label'
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <IconButton
                  onClick={() => setDialogOpen(true)}
                  sx={{
                    border: "1px solid #E0E0E0",
                    p: 2,
                    mb: 1,
                    "&:hover": { bgcolor: "#F5F5F5" },
                  }}
                >
                  <AccessibilityNew sx={{ fontSize: 24, color: "#666" }} />
                </IconButton>
                <Typography variant='body2' color='text.secondary'>
                  Complete body map
                </Typography>
              </Box>
            </Box>
          </Box>
          <ReusableDialog
            isOpen={dialogOpen}
            onClose={() => setDialogOpen(false)}
            onSubmit={() => {
              setBodyMapOpen(true);
              setDialogOpen(false);
            }}
            submitText='Submit'
            cancelText='Cancel'
          >
            <Box>
              <Chip
                variant='contained'
                sx={{
                  fontSize: "14px",
                  backgroundColor: "#F2F6FF",
                  color: "blue",
                }}
                label='Daily Log'
                size='small'
              />
              <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold" }}>Body Skin Map</Typography>
              <Typography variant='subtitle2' sx={{ color: "#757575" }}>
                Date: {new Date().toLocaleDateString()} &nbsp; • &nbsp;{" "}
                <span style={{ color: "#1751D0" }}>{user.username}</span>
              </Typography>
              <Box sx={{ borderBottom: "1px solid #E0E0E0", mt: 2, mb: 2 }} />
              <Typography sx={{ mb: 2 }}>Select the Injured person</Typography>

              <TextField
                select
                fullWidth
                placeholder='Enter the name'
                variant='outlined'
                value={selectedPerson}
                onChange={(e) => setSelectedPerson(e.target.value)}
                sx={{ mb: 3 }}
              >
                <MenuItem value='Male'>Male</MenuItem>
                <MenuItem value='Female'>Female</MenuItem>
              </TextField>
            </Box>
          </ReusableDialog>

          <ReusableDialog
            isOpen={bodyMapOpen}
            onClose={() => setBodyMapOpen(false)}
            onSubmit={() => {
              setSelectedPerson("");
              setBodyMapOpen(false);
              updateFormData({
                imageInfo: incidentRecords,
                uploadedFiles: preparedFiles,
                uploadedImages,
                gender: selectedPerson,
              });
            }}
            submitText='Submit'
            cancelText='Cancel'
            maxWidth='lg'
          >
            {selectedPerson === "Male" ? (
              <MaleBodyMap onIncidentSubmit={handleIncidentSubmit} setUploadedImages={setUploadedImages} />
            ) : selectedPerson === "Female" ? (
              <FemaleBodyMap onIncidentSubmit={handleIncidentSubmit} setUploadedImages={setUploadedImages} />
            ) : (
              <></>
            )}
          </ReusableDialog>
        </Grid>
        <Grid item lg={4} md={4}>
          <Typography variant='h6' sx={{ fontWeight: 600, mb: 1 }}>
            Incident Management
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Sample Description 1
          </Typography>
          <Box sx={{ mt: 1 }}>
            {uploadedFiles.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "50px",
                  bgcolor: "background.paper",
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  No records yet...
                </Typography>
              </Box>
            ) : (
              uploadedFiles.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 1,
                    p: 1,
                    border: "1px solid #E0E0E0",
                    borderRadius: "4px",
                    bgcolor: "background.paper",
                    "&:hover": {
                      bgcolor: "#F5F5F5",
                    },
                  }}
                >
                  <TextField variant='outlined' size='small' value={file.name} disabled fullWidth />
                  <IconButton
                    onClick={() => handleDownload(file)}
                    sx={{
                      "&:hover": {
                        color: "#1751D0",
                      },
                    }}
                  >
                    <Download sx={{ color: "#1751D0" }} />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteFile(index)}
                    sx={{
                      "&:hover": {
                        color: "error.main",
                      },
                    }}
                  >
                    <Delete sx={{ color: "error.main" }} />
                  </IconButton>
                </Box>
              ))
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Footer Actions */}
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 1000,
          display: "flex",
          gap: 2,
        }}
      >
        <Button
          variant='outlined'
          color='primary'
          onClick={() => {
            window.scrollTo(0, 0); // Prevent scrolling
            prevStep();
          }}
        >
          Previous
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            window.scrollTo(0, 0);
            handleSubmit();
          }}
          disabled={incidentRecords.length === 0 || uploadedFiles.length === 0}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default CauseForImage;
